import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/** below this the copy stops being readable; scroll instead of shrinking further */
const MIN_SCALE = 0.64;

/**
 * The step as drawn — all of it, one cluster — scaled down as a unit until it
 * fits above the CTA. Nothing inside a step is laid out separately for the
 * phone; the design's own proportions are kept and the whole thing shrinks.
 *
 * Two passes, then done:
 *   1. at natural size, scale = room / height
 *   2. the inner element is widened by 1/scale so the scaled result still
 *      spans the column; that reflow changes the height (a 16:9 video gets
 *      taller, text gets shorter), so the result is measured once more and
 *      the scale corrected
 * A loader growing while on screen, an image arriving late, or the browser
 * chrome resizing starts the two passes again.
 */
/**
 * `scroll` opts a step out of scaling: it renders at full size and scrolls
 * inside its area. That is right for the plan page — three plans, six bullets,
 * payment marks — where shrinking to fit makes the prices unreadable and a
 * scrolling paywall is what people expect anyway.
 */
export function FitToScreen({ children, resetKey, scroll = false }: { children: ReactNode; resetKey: string; scroll?: boolean }) {
  const outer = useRef<HTMLDivElement | null>(null);
  const inner = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [pass, setPass] = useState(0);
  const passes = useRef(0);
  const settling = useRef(false);

  const remeasure = () => {
    settling.current = true;
    passes.current = 0;
    setScale(1);
    setPass((n) => n + 1);
  };

  useLayoutEffect(() => {
    const box = outer.current;
    const content = inner.current;
    if (!box || !content) return;
    const room = box.clientHeight;
    if (room <= 0) return;

    if (scroll) {
      settling.current = false;
      return;
    }
    if (scale === 1) {
      // pass 1: natural layout
      const needed = content.scrollHeight;
      passes.current = 0;
      if (needed > room) {
        settling.current = true;
        setScale(Math.max(MIN_SCALE, room / needed));
      } else {
        settling.current = false;
      }
      return;
    }
    passes.current += 1;
    const visual = content.getBoundingClientRect().height;
    if (visual > room + 1 && passes.current <= 3) {
      // pass 2/3: the widened layout, measured with its transform applied,
      // still overshoots — shrink once more
      const next = Math.max(MIN_SCALE, (scale * room * 0.99) / visual);
      if (next < scale - 0.002) {
        setScale(next);
        return;
      }
    } else if (visual < room - 16 && passes.current === 1) {
      // widening the layout made the text reflow shorter, so the first scale
      // was too timid; grow back once (never to 1, which would restart pass 1),
      // and the pass after this can only shrink again
      const next = Math.min(0.995, (scale * (room - 6)) / visual);
      if (next > scale + 0.01) {
        setScale(next);
        return;
      }
    }
    settling.current = false;
  }, [scale, pass, resetKey]);

  useLayoutEffect(() => {
    // layout must be reset before paint, so this is deliberately synchronous
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (scroll) setScale(1);
    else remeasure();
  }, [resetKey, scroll]);

  useEffect(() => {
    const onResize = () => remeasure();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(onResize) : null;
    if (ro && outer.current) ro.observe(outer.current);

    // a step that changes size on its own (loaders adding rows): re-fit when
    // the visual result no longer matches the room — but not while our own
    // two passes are still settling, which is what would otherwise loop
    const grow =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(() => {
            if (settling.current) return;
            const box = outer.current;
            const content = inner.current;
            if (!box || !content) return;
            const visual = content.getBoundingClientRect().height;
            const room = box.clientHeight;
            if (visual > room + 1 || visual < room - 48) remeasure();
          })
        : null;
    if (grow && inner.current) grow.observe(inner.current);

    const imgs = [...(inner.current?.querySelectorAll('img') ?? [])].filter((i) => !i.complete);
    imgs.forEach((i) => i.addEventListener('load', onResize, { once: true }));
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      ro?.disconnect();
      grow?.disconnect();
      imgs.forEach((i) => i.removeEventListener('load', onResize));
    };
  }, [resetKey]);

  const scaled = !scroll && scale < 1;
  return (
    <div ref={outer} className="fk-fit" style={{ flex: 1, minHeight: 0, overflowX: 'hidden', overflowY: scroll || scale <= MIN_SCALE ? 'auto' : 'hidden' }}>
      <div
        ref={inner}
        style={{
          transform: scaled ? `scale(${scale})` : undefined,
          transformOrigin: 'top left',
          width: scaled ? `${100 / scale}%` : '100%',
        }}
      >
        {children}
      </div>
    </div>
  );
}
